import {
  Component,
  ElementRef,
  ViewEncapsulation,
  computed,
  effect,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { IconComponent } from 'nebula-ui-kit';

const MASK_CHAR = '\u2022';

/**
 * Entrada de clave sin <input>: captura teclado en un div.
 * Los gestores de contraseñas del navegador no pueden engancharse a este elemento.
 */
@Component({
  selector: 'app-admin-user-secret-field',
  standalone: true,
  imports: [IconComponent],
  encapsulation: ViewEncapsulation.ShadowDom,
  template: `
    <div class="wrap">
      @if (locked()) {
        <button type="button" class="unlock" (mousedown)="unlock($event)">
          Toque aquí para ingresar la clave
        </button>
      } @else {
        <div
          #captureRef
          class="capture"
          tabindex="0"
          role="textbox"
          aria-multiline="false"
          aria-autocomplete="none"
          autocapitalize="off"
          spellcheck="false"
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
          data-form-type="other"
          [attr.aria-label]="ariaLabel()"
          [class.empty]="!internalValue()"
          [attr.data-placeholder]="placeholder()"
          (keydown)="onKeydown($event)"
          (paste)="onPaste($event)"
          (mousedown)="$event.stopPropagation()"
        >
          {{ displayText() }}
        </div>
        <button
          type="button"
          class="toggle"
          (mousedown)="onToggleReveal($event)"
          [attr.aria-label]="reveal() ? 'Ocultar clave' : 'Mostrar clave'"
        >
          <lib-icon [name]="reveal() ? 'EyeOff' : 'Eye'" [size]="18" aria-hidden="true" />
        </button>
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
    }

    .wrap {
      position: relative;
      width: 100%;
    }

    .unlock {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-height: 2.75rem;
      padding: 0 0.75rem;
      border: 1px dashed var(--color-border, #9aa0a6);
      border-radius: 8px;
      background: var(--color-bg-content-secondary, var(--md-sys-color-surface-container-low, #f8f9fa));
      color: var(--color-subtitle, var(--md-sys-color-on-surface-variant, #444746));
      font: inherit;
      font-size: 0.875rem;
      cursor: pointer;
    }

    .unlock:hover {
      background: var(--color-bg-elevated, #f1f3f4);
      border-color: var(--md-sys-color-primary, #0057b8);
      color: var(--md-sys-color-primary, #0057b8);
    }

    .capture {
      box-sizing: border-box;
      width: 100%;
      min-height: 2.75rem;
      padding: 0.65rem 2.5rem 0.65rem 0.75rem;
      border: 1px solid var(--color-border, var(--md-sys-color-outline, #c4c7cf));
      border-radius: 8px;
      background: var(--color-bg-elevated, var(--md-sys-color-surface-container-lowest, #fff));
      color: var(--color-title, var(--md-sys-color-on-surface, inherit));
      font: inherit;
      line-height: 1.4;
      white-space: pre;
      overflow-x: auto;
      overflow-y: hidden;
      cursor: text;
      outline: none;
      user-select: none;
    }

    .capture:focus {
      outline: 2px solid var(--md-sys-color-primary, #0057b8);
      outline-offset: 1px;
    }

    .capture.empty::before {
      content: attr(data-placeholder);
      color: var(--color-subtitle, #666);
      pointer-events: none;
    }

    .toggle {
      position: absolute;
      top: 50%;
      right: 0.35rem;
      transform: translateY(-50%);
      z-index: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      padding: 0;
      border: none;
      background: transparent;
      color: var(--color-subtitle, #6b7280);
      cursor: pointer;
    }
  `,
})
export class AdminUserSecretFieldComponent {
  readonly placeholder = input('');
  readonly ariaLabel = input('Clave de acceso');
  readonly fieldKey = input('');
  readonly resetToken = input(0);
  readonly reveal = input(false);

  readonly valueChange = output<string>();
  readonly toggleReveal = output<void>();

  readonly locked = signal(true);
  readonly internalValue = signal('');
  readonly captureRef = viewChild<ElementRef<HTMLDivElement>>('captureRef');

  readonly displayText = computed(() => {
    const value = this.internalValue();
    if (!value) return '';
    return this.reveal() ? value : MASK_CHAR.repeat(value.length);
  });

  constructor() {
    effect(() => {
      this.resetToken();
      this.fieldKey();
      this.internalValue.set('');
      this.locked.set(true);
    });
  }

  unlock(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.locked.set(false);
    queueMicrotask(() => this.captureRef()?.nativeElement.focus({ preventScroll: true }));
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      return;
    }

    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      this.commitValue(this.internalValue().slice(0, -1));
      return;
    }

    if (event.key === 'Delete') {
      event.preventDefault();
      this.commitValue('');
      return;
    }

    if (event.key.length !== 1) {
      return;
    }

    event.preventDefault();
    this.commitValue(this.internalValue() + event.key);
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = (event.clipboardData?.getData('text/plain') ?? '').replace(/[\r\n\t]/g, '');
    if (!text) return;
    this.commitValue(this.internalValue() + text);
  }

  onToggleReveal(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggleReveal.emit();
    queueMicrotask(() => this.captureRef()?.nativeElement.focus({ preventScroll: true }));
  }

  private commitValue(next: string): void {
    this.internalValue.set(next);
    this.valueChange.emit(next);
  }
}
